// client/src/components/common/UnifiedDateTimeRangePicker.tsx (converted from .js, ST-00204)
//
// Date + time range picker in the visual language of UnifiedDateRangePicker. Emits values via
// onStartChange / onEndChange as datetime-local strings ("YYYY-MM-DDTHH:MM", '' when cleared)
// so it slots in wherever a native <input type="datetime-local"> was used.
//
// One picker, two containers (PickerSurface): From / To, one month of days for the end being
// set, and the hour / minute grids with "Type a time" (PickerTimeChooser, ST-00264); the page's
// quick times, if it gives any, set the end being set. Cancel (Esc, a click outside) keeps the
// page's values, Apply hands both over, Clear (beside the title) empties both. A day picked for
// an end with no time yet takes the whole day: From 00:00, To 23:59 (PLT-16). Apply stays off
// while the end is before the start.
//
// Desktop (ST-00294): a trigger field (its size: ST-00312 below), opening a popup under it
// (PickerPopover) with From / To text boxes on top — a date and a time, e.g. 2026/09/25 07:05 or
// 25/09/2026 0705; a date alone keeps that end's time (or takes the whole day); emptying a box
// leaves that end open; Enter applies — then the calendar and the time chooser side by side.
// It opens with the From text selected. Focusing a box makes its end the one being set.
//
// Phone / touch (ST-00204): a full-width 44 px trigger with a clear "×", opening a bottom sheet
// with From / To tabs instead of text boxes.
//
// ST-00312: the desktop trigger is a form field by default (`size="field"`): 38 px, 14 px text,
// as tall as the inputs, ThemedSelect and the buttons beside it (it was the 26 px, 12 px-text
// filter-bar trigger everywhere, so on a form row — Replay's Trucks · Time range · Replay, the
// parallel run's Name · Window — it sat shorter than its neighbours). `size="compact"` keeps that
// dense trigger for filter bars of 26–30 px controls (Recon's column filters). The phone / touch
// trigger is 44 px either way.

import { useEffect, useRef, useState } from 'react';
import PickerSurface from './PickerSurface';
import PickerCalendar from './PickerCalendar';
import PickerTimeChooser from './PickerTimeChooser';
import PickerTimePresets from './PickerTimePresets';
import PickerTypedField, { type TypedCheck } from './PickerTypedField';
import { revealTime, usePickerSheetMode } from './pickerHooks';
import {
  atTime,
  formatHm,
  formatPickerDate,
  formatPickerDateTime,
  formatTypedDateTime,
  parseLocalValue,
  parseTypedDateTime,
  startOfMonth,
  timeOf,
  toLocalValue,
  type HourMinute,
  type TimePreset,
} from './pickerLogic';

/** Default times when a day is picked for an end that had none: the whole day. */
const START_OF_DAY: HourMinute = { h: 0, m: 0 };
const END_OF_DAY: HourMinute = { h: 23, m: 59 };

type End = 'start' | 'end';

export interface UnifiedDateTimeRangePickerProps {
  /** "YYYY-MM-DDTHH:MM" (or '' / null for none). */
  startValue: string | null | undefined;
  endValue: string | null | undefined;
  onStartChange: (value: string) => void;
  onEndChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Quick-time chips that set the end being set (none by default). */
  presets?: readonly TimePreset[];
  /**
   * The desktop trigger's size: 'field' (default) is a form field, 38 px with 14 px text like the
   * inputs and selects beside it; 'compact' the dense 26 px filter-bar trigger. Phone / touch: 44 px.
   */
  size?: 'field' | 'compact';
}

/** The desktop trigger per size (ST-00312): its box and text, and the calendar icon. */
const TRIGGER_SIZE = {
  field: { box: 'min-h-[38px] gap-2 px-2.5 py-1.5 text-sm', icon: 'w-4 h-4' },
  compact: { box: 'gap-1.5 px-2 py-1 text-xs', icon: 'w-3.5 h-3.5' },
} as const;

const CalendarIcon = ({ className }: { className: string }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const UnifiedDateTimeRangePicker = ({
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  disabled = false,
  placeholder = 'Any date / time',
  presets,
  size = 'field',
}: UnifiedDateTimeRangePickerProps) => {
  const sheetMode = usePickerSheetMode();
  const [isOpen, setIsOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [pendingEnd, setPendingEnd] = useState<Date | null>(null);
  const [typedInvalid, setTypedInvalid] = useState(false);
  // Desktop From / To boxes holding something that cannot be read: Apply stays off.
  const [fieldInvalid, setFieldInvalid] = useState<Record<End, boolean>>({ start: false, end: false });
  const [activeEnd, setActiveEnd] = useState<End>('start');
  const [month, setMonth] = useState<Date>(() => startOfMonth(new Date()));
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const activeDayRef = useRef<HTMLButtonElement | null>(null);
  const fromFieldRef = useRef<HTMLInputElement | null>(null);
  const timeRef = useRef<HTMLDivElement | null>(null);

  // Rotating a phone across the breakpoint while open: close instead of switching popups.
  useEffect(() => {
    setIsOpen(false);
  }, [sheetMode]);

  const close = () => setIsOpen(false);

  const parsedStart = parseLocalValue(startValue);
  const parsedEnd = parseLocalValue(endValue);
  const hasValue = !!(parsedStart || parsedEnd);

  const valueOf = (end: End) => (end === 'start' ? pendingStart : pendingEnd);
  const setValueOf = (end: End) => (end === 'start' ? setPendingStart : setPendingEnd);
  const defaultTimeOf = (end: End) => (end === 'start' ? START_OF_DAY : END_OF_DAY);

  const active = valueOf(activeEnd);
  const setActive = setValueOf(activeEnd);
  const inverted = !!(pendingStart && pendingEnd && pendingEnd < pendingStart);
  const blocked = inverted || typedInvalid || (!sheetMode && (fieldInvalid.start || fieldInvalid.end));

  // Primed in the same render that opens the picker (no stale first frame).
  const openPicker = () => {
    if (disabled) return;
    setPendingStart(parsedStart);
    setPendingEnd(parsedEnd);
    setActiveEnd('start');
    setFieldInvalid({ start: false, end: false });
    setMonth(startOfMonth(parsedStart ?? parsedEnd ?? new Date()));
    setIsOpen(true);
  };

  const handleApply = () => {
    if (blocked) return;
    onStartChange(toLocalValue(pendingStart));
    onEndChange(toLocalValue(pendingEnd));
    setIsOpen(false);
  };

  const handleClear = () => {
    setPendingStart(null);
    setPendingEnd(null);
    onStartChange('');
    onEndChange('');
    setIsOpen(false);
  };

  const showEnd = (end: End) => {
    setActiveEnd(end);
    const d = valueOf(end);
    if (d) setMonth(startOfMonth(d));
  };

  // ── Desktop From / To text boxes ─────────────────────────────────────────

  const fallbackTime = (end: End): HourMinute => {
    const d = valueOf(end);
    return d ? timeOf(d) : defaultTimeOf(end);
  };

  const checkTyped =
    (end: End) =>
    (text: string): TypedCheck =>
      !text.trim() || parseTypedDateTime(text, fallbackTime(end))
        ? { error: '' }
        : { error: 'Type it like 2026/09/25 07:00.', unreadable: true };

  const typeEnd =
    (end: End) =>
    (text: string): string | undefined => {
      if (!text.trim()) {
        // An empty box leaves that end open ("Any").
        setValueOf(end)(null);
        return '';
      }
      const d = parseTypedDateTime(text, fallbackTime(end));
      if (!d) return undefined;
      setValueOf(end)(d);
      setMonth(startOfMonth(d));
      return formatTypedDateTime(d);
    };

  const markInvalid = (end: End) => (bad: boolean) =>
    setFieldInvalid((cur) => (cur[end] === bad ? cur : { ...cur, [end]: bad }));

  const surfaceOpen = isOpen && !disabled;

  const ends = sheetMode ? (
    <div className="picker-ends" role="group" aria-label="Which end you are setting">
      {(['start', 'end'] as const).map((end) => {
        const value = valueOf(end);
        return (
          <button
            key={end}
            type="button"
            className="picker-end"
            aria-pressed={activeEnd === end}
            aria-label={`${end === 'start' ? 'From' : 'To'}: ${value ? formatPickerDateTime(value) : 'not set'}`}
            onClick={() => showEnd(end)}
          >
            <span className="picker-end__label">{end === 'start' ? 'From' : 'To'}</span>
            {value ? (
              <>
                <span className="picker-end__value">{formatPickerDate(value)}</span>
                <span className="picker-end__value picker-end__value--time">
                  {formatHm(timeOf(value))}
                </span>
              </>
            ) : (
              <span className="picker-end__value picker-end__value--empty">Not set</span>
            )}
          </button>
        );
      })}
    </div>
  ) : (
    <div className="picker-typed-row" role="group" aria-label="Which end you are setting">
      {(['start', 'end'] as const).map((end) => {
        const value = valueOf(end);
        return (
          <PickerTypedField
            key={end}
            label={end === 'start' ? 'From' : 'To'}
            value={value ? formatTypedDateTime(value) : ''}
            placeholder="YYYY/MM/DD HH:MM"
            active={activeEnd === end}
            check={checkTyped(end)}
            onType={typeEnd(end)}
            onFocus={() => showEnd(end)}
            onEnter={handleApply}
            onInvalidChange={markInvalid(end)}
            inputRef={end === 'start' ? fromFieldRef : undefined}
            completeLength={16}
          />
        );
      })}
    </div>
  );

  const calendar = (
    <PickerCalendar
      month={month}
      onMonthChange={setMonth}
      selected={pendingStart && pendingEnd ? null : (pendingStart ?? pendingEnd)}
      rangeStart={pendingStart && pendingEnd ? pendingStart : null}
      rangeEnd={pendingStart && pendingEnd ? pendingEnd : null}
      bounds="all"
      min={activeEnd === 'end' ? pendingStart : null}
      max={activeEnd === 'start' ? pendingEnd : null}
      onSelect={(day) => {
        setActive(atTime(day, active ? timeOf(active) : defaultTimeOf(activeEnd)));
        revealTime(timeRef.current);
      }}
      activeDayRef={activeDayRef}
    />
  );

  const time = (
    <PickerTimeChooser
      // Each end has its own typed text.
      key={activeEnd}
      ref={timeRef}
      label={activeEnd === 'start' ? 'From time' : 'To time'}
      value={active ? timeOf(active) : defaultTimeOf(activeEnd)}
      onChange={(t) => setActive(atTime(active ?? new Date(), t))}
      onTypedInvalidChange={setTypedInvalid}
      onEnter={sheetMode ? undefined : handleApply}
    />
  );

  const picker = (
    <PickerSurface
      variant={sheetMode ? 'sheet' : 'popover'}
      open={surfaceOpen}
      title="Select date & time range"
      onCancel={close}
      onApply={handleApply}
      applyDisabled={blocked}
      returnFocusRef={triggerRef}
      initialFocusRef={sheetMode ? activeDayRef : fromFieldRef}
      headerAction={
        hasValue ? (
          <button type="button" className="picker-sheet__text-action" onClick={handleClear}>
            Clear
          </button>
        ) : undefined
      }
    >
      {ends}
      {inverted ? (
        <p className="picker-hint picker-hint--error" role="alert">
          The end is before the start. Change one of them.
        </p>
      ) : (
        <p className="picker-hint" aria-live="polite">
          {activeEnd === 'start' ? 'Pick the first day and time.' : 'Pick the last day and time.'}
        </p>
      )}
      {presets && presets.length > 0 && (
        <PickerTimePresets
          presets={presets}
          value={active}
          limits={{
            min: activeEnd === 'end' ? pendingStart : null,
            max: activeEnd === 'start' ? pendingEnd : null,
          }}
          onPick={(at) => {
            setActive(at);
            setMonth(startOfMonth(at));
          }}
        />
      )}
      {sheetMode ? (
        <>
          {calendar}
          {time}
        </>
      ) : (
        <div className="picker-dt">
          {calendar}
          {time}
        </div>
      )}
    </PickerSurface>
  );

  if (sheetMode) {
    const label = hasValue
      ? `${parsedStart ? formatPickerDateTime(parsedStart) : 'Any'} → ${parsedEnd ? formatPickerDateTime(parsedEnd) : 'Any'}`
      : '';

    return (
      <>
        <div className={`picker-field${disabled ? ' picker-field--disabled' : ''}`}>
          <button
            ref={triggerRef}
            type="button"
            className="picker-field__main"
            onClick={openPicker}
            disabled={disabled}
            aria-haspopup="dialog"
            aria-expanded={surfaceOpen}
            aria-label={
              hasValue
                ? `Date and time: ${label.replace('→', 'to')}. Change`
                : `${placeholder}. Choose dates and times`
            }
          >
            {hasValue ? (
              <span className="picker-field__text">{label}</span>
            ) : (
              <span className="picker-field__text picker-field__placeholder">{placeholder}</span>
            )}
            <CalendarIcon className="picker-field__icon" />
          </button>
          {hasValue && !disabled && (
            <button
              type="button"
              className="picker-field__clear"
              onClick={handleClear}
              aria-label="Clear the dates and times"
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M15 5L5 15M5 5l10 10"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
        {picker}
      </>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={openPicker}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={surfaceOpen}
        data-size={size}
        className={`w-full flex items-center justify-between ${TRIGGER_SIZE[size].box} border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-400 focus:border-slate-400 transition-all duration-200 min-w-0 ${
          disabled
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200'
            : 'border-slate-300 bg-surface cursor-pointer hover:border-slate-400 text-slate-700'
        }`}
      >
        <div className="flex items-center gap-1 flex-1 min-w-0 overflow-hidden">
          {hasValue ? (
            <>
              <span className="truncate">{parsedStart ? formatTypedDateTime(parsedStart) : 'Any'}</span>
              <span className="text-slate-300 flex-shrink-0">→</span>
              <span className="truncate">{parsedEnd ? formatTypedDateTime(parsedEnd) : 'Any'}</span>
            </>
          ) : (
            <span className="text-slate-400 truncate">{placeholder}</span>
          )}
        </div>
        <CalendarIcon className={`${TRIGGER_SIZE[size].icon} text-slate-400 flex-shrink-0`} />
      </button>
      {picker}
    </>
  );
};

export default UnifiedDateTimeRangePicker;
